/**
 * Storefront toasts without shipping `sonner` in every page's initial JavaScript: the first toast
 * (always a reaction to the visitor — add to cart, a form result) loads the library and makes
 * core/components/ui/ToasterLazy.tsx mount the <Toaster>. Every call waits for both, so the first
 * toast is shown as soon as the Toaster is up, not dropped.
 *
 * Fire-and-forget only (no returned id): storefront code never reads it. The admin panel keeps
 * importing `sonner` directly (toast.loading + `{ id }` updates) with the eager Toaster.
 */
type Sonner = typeof import('sonner');
type ToastArgs = Parameters<Sonner['toast']['success']>;

let markReady: () => void = () => {};
const toasterReady = new Promise<void>((resolve) => { markReady = resolve; });

/** Called by the Toaster once mounted (see core/components/ui/toaster.tsx). */
export function markToasterReady() {
    markReady();
}

/** Event the lazy storefront Toaster (core/components/ui/ToasterLazy.tsx) mounts on. */
export const TOAST_REQUESTED_EVENT = 'stag-toast-requested';
let requested = false;
/** True once any toast was asked for — for a Toaster mounting after that moment. */
export function wasToastRequested() {
    return requested;
}

let sonner: Promise<Sonner> | null = null;
const loadSonner = () => {
    if (!requested) {
        requested = true;
        if (typeof window !== 'undefined') window.dispatchEvent(new Event(TOAST_REQUESTED_EVENT));
    }
    return (sonner ??= Promise.all([import('sonner'), toasterReady]).then(([mod]) => mod));
};

const show = (kind: 'success' | 'error' | 'info' | 'warning') => (...args: ToastArgs): void => {
    void loadSonner().then(({ toast }) => { toast[kind](...args); });
};

export const toast = {
    success: show('success'),
    error: show('error'),
    info: show('info'),
    warning: show('warning'),
};
